function titleCase(value) {
  return String(value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getDomainBrandName(hostname) {
  const cleanHost = String(hostname || '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
    .split(':')[0]
    .trim();

  if (!cleanHost || cleanHost === 'localhost') return '';

  const [domainName] = cleanHost.split('.');
  return titleCase(domainName);
}

export function pickResolvedBrandName(resolved = {}) {
  return (
    resolved?.account_details?.business_name ||
    resolved?.account_details?.brand_name ||
    resolved?.account_details?.name ||
    ''
  );
}

const POSBYTZ_POWERED_BY_URL = 'https://posbytz.com/en/';

function toHttpsUrl(domain) {
  const value = String(domain || '').trim();
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

// "Powered by" attribution for the footer. Whitelabel partners get their own
// name + domain; everyone else links to PosBytz with the storefront host as
// utm_source so referrals are attributed per merchant (web and native).
function isTruthyFlag(value) {
  if (typeof value === 'string') return ['yes', 'true', '1'].includes(value.trim().toLowerCase());
  return Boolean(value);
}

// The first active domain of type APP.
function pickPartnerDomain(domains) {
  const app = (Array.isArray(domains) ? domains : []).find(
    (d) => d?.domain_name && d.is_active !== false && String(d.type || '').toUpperCase() === 'APP',
  );
  return app?.domain_name || '';
}

// Strip subdomains down to the registrable domain so the link lands on the
// partner's root site: admin-partner.posbytz.dev → posbytz.dev,
// studio.alia.co.in → alia.co.in.
function toRootDomain(domain) {
  const host = String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .split(/[/?#:]/)[0]
    .toLowerCase();
  const labels = host.split('.').filter(Boolean);
  if (labels.length <= 2) return host;
  const [sld, tld] = labels.slice(-2);
  const isTwoPartSuffix =
    tld.length === 2 && ['co', 'com', 'net', 'org', 'gov', 'ac', 'edu'].includes(sld);
  return labels.slice(isTwoPartSuffix ? -3 : -2).join('.');
}

export function pickResolvedPoweredBy(resolved = {}, { sourceHost = '' } = {}) {
  const partner = resolved?.partner_details || {};
  const isWhitelabel = isTruthyFlag(resolved?.white_labeled ?? partner.is_white_labeled);
  const partnerName = String(partner.business_name || partner.name || '').trim();
  const partnerUrl = toHttpsUrl(toRootDomain(pickPartnerDomain(partner.partner_domains)));

  if (isWhitelabel && partnerName) {
    return { name: partnerName, url: partnerUrl };
  }

  const url = new URL(POSBYTZ_POWERED_BY_URL);
  url.searchParams.set(
    'utm_source',
    String(sourceHost || '').replace(/^www\./i, '') || 'storefront',
  );
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', 'powered_by_posbytz');
  return { name: 'PosBytz', url: url.toString() };
}

export function pickResolvedLogoUrl(resolved = {}) {
  return (
    resolved?.account_details?.theme_settings?.logo_url ||
    resolved?.account_details?.theme_settings?.logo?.url ||
    resolved?.account_details?.settings?.theme_settings?.logo_url ||
    resolved?.account_details?.settings?.theme_settings?.logo?.url ||
    resolved?.account_details?.logo?.url ||
    resolved?.account_details?.logo_url ||
    resolved?.account_details?.logoUrl ||
    '/pwa-192x192.png'
  );
}

export function formatList(val, keys = []) {
  if (!val) return '';
  return (Array.isArray(val) ? val : [val])
    .map((i) => (typeof i === 'string' ? i : keys.reduce((acc, k) => acc || i?.[k], '') || ''))
    .filter(Boolean)
    .join(', ');
}

export function formatPhoneList(val) {
  if (!val) return '';
  return (Array.isArray(val) ? val : [val])
    .map((i) =>
      typeof i === 'string'
        ? i
        : i?.calling_code && i?.number
          ? `+${i.calling_code}-${i.number}`
          : i?.full_number || i?.number || '',
    )
    .filter(Boolean)
    .join(', ');
}

export function parseStringList(val) {
  return String(val || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatAddress(addr) {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.trim();
  if (typeof addr === 'object' && addr !== null) {
    if (typeof addr.formatted_address === 'string' && addr.formatted_address.trim()) {
      return addr.formatted_address.trim();
    }
    if (typeof addr.full_address === 'string' && addr.full_address.trim()) {
      return addr.full_address.trim();
    }

    const parts = [
      addr.building_number || addr.buildingNumber || addr.building_name || addr.building || '',
      addr.plot_identification_number ||
        addr.plot_number ||
        addr.plotNumber ||
        addr.plotIdentificationNumber ||
        '',
      addr.address_line_1 ||
        addr.address_line1 ||
        addr.addressLine1 ||
        addr.line1 ||
        addr.street ||
        '',
      addr.address_line_2 ||
        addr.address_line2 ||
        addr.addressLine2 ||
        addr.line2 ||
        addr.landmark ||
        addr.area ||
        '',
      addr.city || '',
      addr.state || '',
      addr.zip_code || addr.zipCode || addr.zip || addr.pincode || addr.postal_code || '',
      addr.country || '',
    ]
      .filter((p) => typeof p === 'string' && p.trim() !== '')
      .map((p) => p.trim());

    return parts.join(', ');
  }
  return '';
}
